/**
 * The parts of the onboarding draft that name a bank.
 *
 * Structurally typed rather than importing either side's own type. It runs in
 * the browser against the onboarding context's state and on the server against
 * the stored record's draft, and those must agree on what removing a bank means
 * — a second copy would start agreeing and stop agreeing without anyone noticing.
 */
export interface BankBearingDraft {
  connectedBanks: { id: string }[]
  selectedAccounts: { bankId: string }[]
  pendingBankAccounts: { itemId: string }[]
  excludedBankIds?: string[]
  syncDateConfigByBank: Record<string, unknown>
  resolvedRanges?: Record<string, unknown>
}

/** Whether any persisted part of a draft still names this bank. */
export function draftContainsBank(draft: BankBearingDraft, bankId: string): boolean {
  return (
    draft.connectedBanks.some((bank) => bank.id === bankId) ||
    draft.selectedAccounts.some((account) => account.bankId === bankId) ||
    draft.pendingBankAccounts.some((account) => account.itemId === bankId) ||
    Object.prototype.hasOwnProperty.call(draft.syncDateConfigByBank, bankId) ||
    Object.keys(draft.resolvedRanges ?? {}).some((key) => key.startsWith(`${bankId}|`))
  )
}

/**
 * Removal ids that a successful save did not actually persist.
 *
 * A route-transition save can finish after the dedicated removal request failed.
 * Keeping only ids still present in that exact saved draft lets the provider
 * release its reload guard without falsely acknowledging a stale snapshot.
 */
export function remainingUnsavedBankRemovals(
  draft: BankBearingDraft,
  bankIds: Iterable<string>
): string[] {
  return [...bankIds].filter((bankId) => draftContainsBank(draft, bankId))
}

/**
 * Every trace of one bank, removed from a draft.
 *
 * `pendingBankAccounts` is included, and that is the whole reason this exists as
 * its own function. The queue keeps its last bank on purpose — SelectSyncDate
 * advances it only when another bank follows, because emptying it leaves that
 * screen with no current bank and trips its own out-of-order guard. So removing
 * the bank the user was last working on left the queue still naming it: Back
 * reached SelectSyncDate, whose guard saw nothing selected for that bank and
 * redirected to SelectCardPage, which renders straight off the queue. The
 * revoked bank's accounts were listed again, tickable, and could be carried into
 * a spreadsheet that no live connection backs.
 *
 * Applied in three places: to the tab's own state, to the draft the server hands
 * back when a save is refused as a stale edit, and to the stored record when the
 * disconnect endpoint reconciles it. The last is what makes the removal correct
 * rather than merely recoverable — the others are recovery UX around it.
 */
export function draftWithoutBank<T extends BankBearingDraft>(draft: T, bankId: string): T {
  const syncDateConfigByBank = { ...draft.syncDateConfigByBank }
  delete syncDateConfigByBank[bankId]
  const resolvedRanges = draft.resolvedRanges
    ? Object.fromEntries(
        Object.entries(draft.resolvedRanges).filter(([key]) => !key.startsWith(`${bankId}|`))
      )
    : undefined
  return {
    ...draft,
    connectedBanks: draft.connectedBanks.filter((bank) => bank.id !== bankId),
    selectedAccounts: draft.selectedAccounts.filter((account) => account.bankId !== bankId),
    pendingBankAccounts: draft.pendingBankAccounts.filter((account) => account.itemId !== bankId),
    ...(draft.excludedBankIds
      ? { excludedBankIds: draft.excludedBankIds.filter((id) => id !== bankId) }
      : {}),
    syncDateConfigByBank,
    ...(resolvedRanges ? { resolvedRanges } : {}),
  }
}

/** Skip a connected bank for this onboarding setup without disconnecting it. */
export function excludeBankFromOnboarding<T extends BankBearingDraft>(
  draft: T,
  bankId: string
): T & { excludedBankIds: string[] } {
  const withoutBank = draftWithoutBank(draft, bankId)
  return {
    ...withoutBank,
    excludedBankIds: [...new Set([...(draft.excludedBankIds ?? []), bankId])],
  }
}
